import Page, { PageProps } from '@/components/page';
import { Box } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Panel from '@/components/panel';
import PanelContent from '@/components/panel/panel-content';
import MetabaseContainer from '@/components/metabase/metabase-container';
import { InteractiveDashboard } from '@surepathai/embedding-sdk-react';
import useMetabase from '@/hooks/use-metabase';

const BUFFER_PX = 48;

const MetabaseInsights: FC<PageProps> = () => {
  const { dashboards } = useMetabase();
  const { id: dashboardId } = dashboards.insights;
  const containerRef = useRef<HTMLDivElement>(null);
  const [questionHeight, setQuestionHeight] = useState(840);

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const { top } = container.getBoundingClientRect();
    const available = window.innerHeight - top - BUFFER_PX;
    const next = Math.max(400, Math.floor(available));
    if (Number.isFinite(next)) {
      setQuestionHeight(next);
    }
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  // Override Metabase's minHeight: 100vh inline style
  const overrideMinHeight = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const element = container.querySelector('.mb-mantine-Flex-root') as HTMLElement;
    if (element?.style.minHeight === '100vh') {
      element.style.setProperty('min-height', 'auto', 'important');
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    overrideMinHeight();
    const observer = new MutationObserver(overrideMinHeight);
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });

    return () => observer.disconnect();
  }, [overrideMinHeight]);

  const drillThroughQuestionProps = useMemo(
    () => ({
      height: questionHeight,
      title: true,
      isSaveEnabled: false,
      withDownloads: true,
    }),
    [questionHeight]
  );

  return (
    <Panel>
      <PanelContent>
        <Box ref={containerRef}>
          <MetabaseContainer>
            <InteractiveDashboard
              dashboardId={dashboardId}
              className="metabase-embed"
              withSubscriptions
              withTitle={false}
              withDownloads={true}
              drillThroughQuestionHeight={questionHeight}
              drillThroughQuestionProps={drillThroughQuestionProps}
            />
          </MetabaseContainer>
        </Box>
      </PanelContent>
    </Panel>
  );
};

const InsightsPage: FC<PageProps> = (props) => {
  return (
    <Page title="Insights" fullHeight={false}>
      <MetabaseInsights {...props} />
    </Page>
  );
};

export default InsightsPage;
